import * as React from 'react';
import {Form} from 'antd';
import {FormComponentProps} from 'antd/lib/form';

import {InventoryStock} from '../api/dtos/dtos';
import locales from '../locales';
import InputFormItem from '../common/form-items/InputFormItem';
import CheckBoxFormItem from '../common/form-items/CheckBoxFormItem';
import SubmitCancelFormItem from '../common/form-items/SubmitCancelFormItem';
import StaticFormItem from '../common/form-items/StaticFormItem';
import {formatDateTimeOrNull} from '../utils/date-utils';
import AutoCompleteFormItem from "../common/form-items/AutoCompleteFormItem";
import NumberFormItem from "../common/form-items/NumberFormItem";
import FormItem from "antd/lib/form/FormItem";
import * as formUtils from "../utils/form-utils";
import {renderFixedPriceColumn} from "../utils/number-utils";
import {CheckboxChangeEvent} from "antd/lib/checkbox";

interface Props extends FormComponentProps {
  stock?: InventoryStock;
  brands: string[];
  types: string[];
  names: string[];
  onFormSubmit?: (stock: InventoryStock) => void;
  onFormCancel: () => void;
}

interface State {
  showInfiniteInputs: boolean;
  cost: number;
  profitMargin: number;
}

const FIELD_INVENTORY_NUMBER = 'inventoryNumber';
const FIELD_NAME = 'name';
const FIELD_BRAND = 'brand';
const FIELD_TYPE = 'type';
const FIELD_INFINITE_STOCK = 'infiniteStock';
const FIELD_INFINITE_STOCK_COST = 'infiniteStockCost';
const FIELD_INFINITE_STOCK_PROFIT_MARGIN = 'infiniteStockProfitMargin';

class InventoryStockForm extends React.Component<Props, State> {

  constructor(props: Props) {
    super(props);
    this.state = {
      showInfiniteInputs: props.stock !== undefined && props.stock.infiniteStock,
      cost: props.stock?.infiniteStockCost ?? 0,
      profitMargin: props.stock?.infiniteStockProfitMargin ?? 0,
    };
  }

  public render() {
    const {form, stock, brands, types, names} = this.props;
    const disabled = stock !== undefined;

    return (
      <Form>
        {stock && (
          <StaticFormItem
            label={locales.created_at}
            initialValue={formatDateTimeOrNull(stock.createdAt) || '-'}
          />
        )}

        <InputFormItem
          form={form}
          fieldId={FIELD_INVENTORY_NUMBER}
          label={locales.item_number}
          initialValue={stock ? stock.inventoryNumber : null}
          required={true}
          disabled={disabled}
        />

        <AutoCompleteFormItem
          form={form}
          fieldId={FIELD_NAME}
          label={locales.name}
          initialValue={stock ? stock.name : null}
          required={true}
          dataSource={names}
        />

        <AutoCompleteFormItem
          form={form}
          fieldId={FIELD_BRAND}
          label={locales.brand}
          initialValue={stock ? stock.brand : null}
          required={true}
          dataSource={brands}
        />

        <AutoCompleteFormItem
          form={form}
          fieldId={FIELD_TYPE}
          label={locales.type}
          initialValue={stock ? stock.type : null}
          required={true}
          dataSource={types}
        />

        <CheckBoxFormItem
          form={form}
          fieldId={FIELD_INFINITE_STOCK}
          label={locales.infinite_stock}
          initialValue={stock ? stock.infiniteStock : false}
          required={false}
          disabled={disabled}
          onChange={this.onInfiniteChanged}
        />

        {this.state.showInfiniteInputs && (<>
            <NumberFormItem
              form={form}
              fieldId={FIELD_INFINITE_STOCK_COST}
              label={locales.initial_cost}
              initialValue={stock ? stock.infiniteStockCost : null}
              required={true}
              min={0}
              precision={0}
              onChange={this.onCostChanged}
            >
              <> {locales.app_currency}</>
            </NumberFormItem>

            <NumberFormItem
              form={form}
              fieldId={FIELD_INFINITE_STOCK_PROFIT_MARGIN}
              label={locales.profit_margin_percent}
              initialValue={stock ? stock.infiniteStockProfitMargin : null}
              required={true}
              min={0}
              precision={0}
              onChange={this.onProfitMarginChanged}
            >
              <> %</>
            </NumberFormItem>

            <FormItem {...formUtils.formItemLayout} label={locales.selling_price}>
              <span
                className="ant-form-text">{renderFixedPriceColumn(this.getSellingPrice())}</span>
            </FormItem>
          </>
        )}

        <SubmitCancelFormItem onSubmit={this.onFormSubmit} onCancel={this.onFormCancel}/>
      </Form>
    );
  }

  private onFormCancel = () => {
    this.props.form.resetFields();
    this.props.onFormCancel();
  };

  private onInfiniteChanged = (e: CheckboxChangeEvent) => {
    this.setState({showInfiniteInputs: e.target.checked});
  }

  private onCostChanged = (cost: number | undefined) => {
    this.setState({cost: cost || 0});
  };

  private onProfitMarginChanged = (profitMargin: number | undefined) => {
    this.setState({profitMargin: profitMargin || 0});
  };

  private getSellingPrice = (): number => {
    const {cost, profitMargin} = this.state;
    return cost * (1 + profitMargin / 100);
  };


  private onFormSubmit = () => {
    const {stock, form, onFormSubmit} = this.props;
    form.validateFieldsAndScroll((err, values) => {
      if (!err && onFormSubmit !== undefined) {
        onFormSubmit({
          ...stock,
          inventoryNumber: values[FIELD_INVENTORY_NUMBER],
          name: values[FIELD_NAME],
          brand: values[FIELD_BRAND],
          type: values[FIELD_TYPE],
          infiniteStock: values[FIELD_INFINITE_STOCK],
          infiniteStockCost: values[FIELD_INFINITE_STOCK_COST],
          infiniteStockProfitMargin: values[FIELD_INFINITE_STOCK_PROFIT_MARGIN]
        });
      }
    });
  };
}

export default Form.create<Props>()(InventoryStockForm);




// WEBPACK FOOTER //
// ./src/inventory-stock-details/InventoryStockForm.tsx